/**
 * Frontend API service for backup/restore operations.
 * Wraps IPC calls to the Electron main process.
 */

import type {
  MssqlExportRequest,
  MssqlExportResult,
  MssqlImportRequest,
  MssqlImportResult,
  MssqlBackupBakRequest,
  MssqlBackupBakResult,
  MssqlDefaultBackupDirectoryResult,
  MssqlListServerFilesRequest,
  MssqlListServerFilesResult,
  MssqlRestoreBakRequest,
  MssqlRestoreBakResult,
  PgsqlBackupRequest,
  PgsqlBackupResult,
  PgsqlRestoreRequest,
  PgsqlRestoreResult,
  PgsqlRestoreFormat,
  BackupTaskProgress,
} from '../types/backupRestore';

/**
 * Export an MSSQL database to a .bacpac file.
 * Opens a save dialog if no filePath is provided.
 */
export async function mssqlExport(request: MssqlExportRequest): Promise<MssqlExportResult> {
  if (!window.electronAPI?.backupRestore?.mssqlExport) {
    return { success: false, error: 'Backup/Restore API is unavailable' };
  }

  return window.electronAPI.backupRestore.mssqlExport(request);
}

/**
 * Import a .bacpac file to create a new MSSQL database.
 * Opens a file dialog if no filePath is provided.
 */
export async function mssqlImport(request: MssqlImportRequest): Promise<MssqlImportResult> {
  if (!window.electronAPI?.backupRestore?.mssqlImport) {
    return { success: false, error: 'Backup/Restore API is unavailable' };
  }

  return window.electronAPI.backupRestore.mssqlImport(request);
}

/**
 * Backup an MSSQL database to a .bak file using native T-SQL BACKUP DATABASE.
 * Note: The backup file is created on the SQL Server machine, not locally.
 */
export async function mssqlBackupBak(request: MssqlBackupBakRequest): Promise<MssqlBackupBakResult> {
  if (!window.electronAPI?.backupRestore?.mssqlBackupBak) {
    return { success: false, error: 'Backup/Restore API is unavailable' };
  }

  return window.electronAPI.backupRestore.mssqlBackupBak(request);
}

/**
 * Restore an MSSQL database from a .bak file using native T-SQL RESTORE DATABASE.
 * Note: The backup file must be on the SQL Server machine, not locally.
 */
export async function mssqlRestoreBak(request: MssqlRestoreBakRequest): Promise<MssqlRestoreBakResult> {
  if (!window.electronAPI?.backupRestore?.mssqlRestoreBak) {
    return { success: false, error: 'Backup/Restore API is unavailable' };
  }

  return window.electronAPI.backupRestore.mssqlRestoreBak(request);
}

/**
 * Cancel a running MSSQL .bak backup/restore task.
 */
export async function mssqlCancelBak(
  taskId: string
): Promise<{ success: boolean; error?: string }> {
  if (!window.electronAPI?.backupRestore?.mssqlCancelBak) {
    return { success: false, error: 'Backup/Restore API is unavailable' };
  }

  return window.electronAPI.backupRestore.mssqlCancelBak(taskId);
}

/**
 * Get the SQL Server instance default backup directory (server-side).
 */
export async function mssqlGetDefaultBackupDirectory(
  connectionId: string
): Promise<MssqlDefaultBackupDirectoryResult> {
  if (!window.electronAPI?.backupRestore?.mssqlGetDefaultBackupDirectory) {
    return { success: false, error: 'Backup/Restore API is unavailable' };
  }

  return window.electronAPI.backupRestore.mssqlGetDefaultBackupDirectory(connectionId);
}

/**
 * List files/directories on the SQL Server machine (best-effort; permissions apply).
 */
export async function mssqlListServerFiles(
  request: MssqlListServerFilesRequest
): Promise<MssqlListServerFilesResult> {
  if (!window.electronAPI?.backupRestore?.mssqlListServerFiles) {
    return { success: false, error: 'Backup/Restore API is unavailable' };
  }

  return window.electronAPI.backupRestore.mssqlListServerFiles(request);
}

/**
 * Check if a database exists on the server.
 */
export async function checkDatabaseExists(
  connectionId: string,
  databaseName: string
): Promise<{ exists: boolean; error?: string }> {
  if (!window.electronAPI?.backupRestore?.checkDatabaseExists) {
    return { exists: false, error: 'Backup/Restore API is unavailable' };
  }

  return window.electronAPI.backupRestore.checkDatabaseExists(connectionId, databaseName);
}

/**
 * Backup a PostgreSQL database using pg_dump.
 * Opens a save dialog if no filePath is provided.
 */
export async function pgsqlBackup(request: PgsqlBackupRequest): Promise<PgsqlBackupResult> {
  if (!window.electronAPI?.backupRestore?.pgsqlBackup) {
    return { success: false, error: 'Backup/Restore API is unavailable' };
  }

  return window.electronAPI.backupRestore.pgsqlBackup(request);
}

/**
 * Check if PostgreSQL tools (pg_dump) are available.
 * @param customBinPath Optional custom path to PostgreSQL bin directory
 */
export async function pgsqlCheckTools(customBinPath?: string): Promise<{
  available: boolean;
  path?: string;
  version?: string;
  error?: string;
}> {
  if (!window.electronAPI?.backupRestore?.pgsqlCheckTools) {
    return { available: false, error: 'Backup/Restore API is unavailable' };
  }

  return window.electronAPI.backupRestore.pgsqlCheckTools(customBinPath);
}

/**
 * Subscribe to backup task progress events.
 * Returns a cleanup function to unsubscribe.
 */
export function subscribeToTaskProgress(
  callback: (progress: BackupTaskProgress) => void
): () => void {
  if (!window.electronAPI?.backupRestore?.onTaskProgress) {
    console.warn('[backupRestoreApi] Task progress subscription unavailable');
    return () => {};
  }

  return window.electronAPI.backupRestore.onTaskProgress(callback);
}

/**
 * Restore a PostgreSQL database using pg_restore or psql.
 * Opens a file dialog if no filePath is provided.
 */
export async function pgsqlRestore(
  request: PgsqlRestoreRequest
): Promise<PgsqlRestoreResult & { detectedFormat?: PgsqlRestoreFormat }> {
  if (!window.electronAPI?.backupRestore?.pgsqlRestore) {
    return { success: false, error: 'Backup/Restore API is unavailable' };
  }

  return window.electronAPI.backupRestore.pgsqlRestore(request);
}

/**
 * Check if PostgreSQL restore tools (pg_restore, psql) are available.
 * @param customBinPath Optional custom path to PostgreSQL bin directory
 */
export async function pgsqlCheckRestoreTools(customBinPath?: string): Promise<{
  available: boolean;
  pgRestorePath?: string;
  psqlPath?: string;
  error?: string;
}> {
  if (!window.electronAPI?.backupRestore?.pgsqlCheckRestoreTools) {
    return { available: false, error: 'Backup/Restore API is unavailable' };
  }

  return window.electronAPI.backupRestore.pgsqlCheckRestoreTools(customBinPath);
}
